import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from '../i18n';
import { apiClient } from '../api/client';
import { useAuthStore } from '../store/authStore';
import { BottomNav } from '../components/ui/BottomNav';
import { DirectionalIcon } from '../components/ui/DirectionalIcon';

export default function FeedbackPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [feedbackText, setFeedbackText] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submitMutation = useMutation({
    mutationFn: () =>
      apiClient.post('/feedbacks', {
        feedback_text: feedbackText,
      }),
    onSuccess: () => {
      setFeedbackText('');
      setSubmitError(null);
      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 3000);
    },
    onError: (err: unknown) => {
      const message = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message || t('failed_submit');
      setSubmitError(message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackText.trim()) {
      setSubmitError(t('empty_feedback'));
      return;
    }
    submitMutation.mutate();
  };

  if (!user) return null;

  return (
    <div className="page" style={{ maxWidth: '430px', margin: '0 auto' }}>
      {/* Top bar — uses the shared .topbar class (in components.css) so the
          safe-area-inset-top padding stays consistent with the other tab
          pages. The translucent backdrop is layered on via inline style;
          padding-inline overrides the class default (24px) to match this
          page's original 16px gutter. */}
      <header
        className="topbar"
        style={{
          position:             'sticky',
          top:                  0,
          zIndex:               50,
          display:              'flex',
          alignItems:           'center',
          justifyContent:       'space-between',
          paddingInline:        '16px',
          backgroundColor:      'rgba(248,249,250,0.85)',
          backdropFilter:       'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          boxShadow:            '0 12px 32px -4px rgba(43,52,55,0.06)',
          boxSizing:            'border-box',
        } as React.CSSProperties}
      >
        <button
          onClick={() => navigate('/profile')}
          style={{
            width: '40px',
            height: '40px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9999px',
            flexShrink: 0,
          }}
          aria-label={t('back_to_profile')}
        >
          <DirectionalIcon icon="arrow_back" style={{ fontSize: '24px', color: '#005bc0' }} />
        </button>
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 114 26"
            style={{ height: '26px', width: 'auto', display: 'block' }}
            aria-label="Lot'N'Go"
          >
            <text
              x="57"
              y="20"
              fontFamily="Manrope, system-ui, sans-serif"
              fontWeight="700"
              fontSize="20"
              fill="#005bc0"
              textAnchor="middle"
              letterSpacing="-0.4"
            >
              {"Lot'N'Go"}
            </text>
          </svg>
        </div>
        <div style={{ width: '40px', flexShrink: 0 }} />
      </header>

      <div className="page-body">
        {/* Page Title */}
        <div style={{ marginBottom: '24px', paddingTop: '8px' }}>
          <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>{t('send_feedback')}</h1>
          <p style={{ fontSize: '13px', color: 'var(--on-surface-2)' }}>
            {t('help_improve')}
          </p>
        </div>

        {/* Success Message */}
        {submitSuccess && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#dcfce7',
              border: '1px solid #86efac',
              borderRadius: '8px',
              marginBottom: '16px',
              color: '#166534',
              fontSize: '14px',
            }}
          >
            ✓ {t('thank_you')}
          </div>
        )}

        {/* Error Message */}
        {submitError && (
          <div
            style={{
              padding: '12px 16px',
              backgroundColor: '#fee2e2',
              border: '1px solid #fca5a5',
              borderRadius: '8px',
              marginBottom: '16px',
              color: '#991b1b',
              fontSize: '14px',
            }}
          >
            ✕ {submitError}
          </div>
        )}

        {/* Feedback Form */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div>
            <label
              htmlFor="feedback"
              style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--on-surface)',
              }}
            >
              {t('your_feedback')}
            </label>
            <textarea
              id="feedback"
              value={feedbackText}
              onChange={(e) => {
                setFeedbackText(e.target.value);
                setSubmitError(null);
              }}
              placeholder={t('feedback_placeholder')}
              style={{
                width: '100%',
                minHeight: '160px',
                padding: '12px 16px',
                borderRadius: '12px',
                border: '1px solid var(--on-surface-2)',
                backgroundColor: 'var(--surface)',
                color: 'var(--on-surface)',
                fontFamily: 'inherit',
                fontSize: '14px',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
              maxLength={5000}
              disabled={submitMutation.isPending}
            />
            <div
              style={{
                marginTop: '4px',
                fontSize: '12px',
                color: 'var(--on-surface-2)',
                textAlign: 'right',
              }}
            >
              {t('character_count', { count: feedbackText.length })}
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitMutation.isPending || !feedbackText.trim()}
            style={{
              padding: '12px 24px',
              borderRadius: '12px',
              border: 'none',
              backgroundColor: submitMutation.isPending ? '#d1d5db' : 'var(--primary)',
              color: submitMutation.isPending ? '#6b7280' : 'white',
              fontWeight: 600,
              fontSize: '14px',
              cursor: submitMutation.isPending || !feedbackText.trim() ? 'not-allowed' : 'pointer',
              transition: 'opacity 200ms',
              opacity: submitMutation.isPending || !feedbackText.trim() ? 0.6 : 1,
            }}
          >
            {submitMutation.isPending ? t('submitting') : t('submit')}
          </button>
        </form>

        {/* Info Box */}
        <div
          style={{
            marginTop: '32px',
            padding: '16px',
            backgroundColor: '#f0f9ff',
            borderRadius: '12px',
            border: '1px solid #bfdbfe',
          }}
        >
          <p style={{ fontSize: '13px', color: '#1e40af', margin: 0 }}>
            <strong>{t('feedback_tip')}</strong>
          </p>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
